'use client';

import { memo, useCallback, type MutableRefObject, type PointerEvent, type MouseEvent } from 'react';
import Image from 'next/image';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from '@/components/ui/context-menu';
import { FM_CONTEXT_MENU_SURFACE } from '@/components/ui/menu-surface';
import { useImageVariant } from '@/lib/image-variant';

const GAP_PX = 24;
const COLS = 4;
const TILE_BUTTON_WIDTH_PX = 100;

type TFn = (key: string, opts?: Record<string, unknown>) => string;

export type ImageComparePreviewProps = {
  tiles: string[];
  tileWidthPx: number;
  tileHeightPx: number;
  /** multiPicker.menuRef —— 让 picker 的全局 pointerdown click-outside 检查命中本面板 */
  menuRef: MutableRefObject<HTMLDivElement | null>;
  onSetMain: (url: string) => void;
  onSplit: (url: string) => void;
  t: TFn;
};

// 只拦截主键（左键）的 down 阶段，放行右键 / 中键 —— 否则右键点按钮时浏览器不会派发 contextmenu，
// 导致瓦片上的 ContextMenu 在按钮区域弹不出来。
const stopPrimaryDown = (e: MouseEvent | PointerEvent) => {
  if ('button' in e && e.button !== 0) return;
  e.preventDefault();
  e.stopPropagation();
};
const stopAll = (e: MouseEvent | PointerEvent) => {
  e.preventDefault();
  e.stopPropagation();
};

const ImageComparePreviewInner = (props: ImageComparePreviewProps) => {
  const { tiles, tileWidthPx, tileHeightPx, menuRef, onSetMain, onSplit, t } = props;

  // Ref callback：组件卸载时 React 会以 null 回调，自动清理
  const setMenuRef = useCallback(
    (el: HTMLDivElement | null) => {
      menuRef.current = el;
    },
    [menuRef],
  );

  // Supabase 图像变换的目标尺寸：按设备像素比放大一倍，避免 retina 屏模糊；
  // 同时设上限避免过大请求。
  const dpr =
    typeof window !== 'undefined' && Number.isFinite(window.devicePixelRatio)
      ? Math.min(3, Math.max(1, window.devicePixelRatio))
      : 1.5;
  const requestHeightPx = Math.min(2048, Math.round(tileHeightPx * dpr));

  return (
    <div
      ref={setMenuRef}
      className="pointer-events-none absolute top-0 left-0 z-30"
      style={{
        width: `${COLS * tileWidthPx + (COLS - 1) * GAP_PX}px`,
      }}
    >
      <div
        className="grid"
        style={{
          gridTemplateColumns: `repeat(${COLS}, ${tileWidthPx}px)`,
          gap: `${GAP_PX}px`,
        }}
      >
        {/* 占位：让出主节点所在的左上角格子，让换行后的第二行第一格与主节点左缘对齐 */}
        <div aria-hidden style={{ width: `${tileWidthPx}px`, height: `${tileHeightPx}px` }} />
        {tiles.map((u, idx) => (
          <ImageCompareTile
            // 仅以 URL 作 key，避免主图切换导致同 URL 瓦片重挂载、Image 重新加载
            key={u}
            url={u}
            index={idx}
            widthPx={tileWidthPx}
            heightPx={tileHeightPx}
            requestHeightPx={requestHeightPx}
            onSetMain={onSetMain}
            onSplit={onSplit}
            t={t}
          />
        ))}
      </div>
    </div>
  );
};

export const ImageComparePreview = memo(ImageComparePreviewInner);

type TileProps = {
  url: string;
  index: number;
  widthPx: number;
  heightPx: number;
  requestHeightPx: number;
  onSetMain: (url: string) => void;
  onSplit: (url: string) => void;
  t: TFn;
};

const ImageCompareTileInner = ({
  url,
  index,
  widthPx,
  heightPx,
  requestHeightPx,
  onSetMain,
  onSplit,
  t,
}: TileProps) => {
  const handleSetMain = useCallback(() => onSetMain(url), [onSetMain, url]);
  const handleSplit = useCallback(() => onSplit(url), [onSplit, url]);

  const handleSetMainClick = useCallback(
    (e: MouseEvent) => {
      stopAll(e);
      handleSetMain();
    },
    [handleSetMain],
  );
  const handleSplitClick = useCallback(
    (e: MouseEvent) => {
      stopAll(e);
      handleSplit();
    },
    [handleSplit],
  );

  // 优先使用 media 服务预生成的缩略图（缺失时回退原图），不需要 Next.js Image 再优化一层
  const { src: variantSrc, onError: onImageError } = useImageVariant(url, {
    height: requestHeightPx,
    resize: 'contain',
    quality: 80,
  });
  const src = variantSrc || url;

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        <div
          className="pointer-events-auto group relative overflow-hidden rounded-3xl bg-neutral-950 shadow-xl shadow-black/30 ring-1 ring-white/10"
          style={{ width: `${widthPx}px`, height: `${heightPx}px` }}
          // Radix ContextMenuTrigger 已会在 portal 内打开菜单；阻止冒泡避免画布根层重复弹出
          onContextMenu={(e) => e.stopPropagation()}
        >
          <Image
            src={src}
            alt={t('Compare image', { index: index + 2 })}
            fill
            sizes={`${Math.round(widthPx)}px`}
            className="object-contain"
            loading="lazy"
            unoptimized
            onError={onImageError}
          />
          <div className="pointer-events-none absolute top-2 left-2 rounded-full bg-black/45 px-2 py-0.5 text-[11px] font-semibold text-white backdrop-blur">
            #{index + 2}
          </div>
          {/* 左下角：设为主图（尺寸与右上角 +N 标签一致：100px × 16/9） */}
          <button
            type="button"
            className="absolute bottom-3 left-3 inline-flex items-center justify-center gap-2 aspect-[16/9] px-3 rounded-2xl border border-white/10 bg-zinc-950/25 backdrop-blur-xl text-[14px] font-semibold tracking-tight text-white/90 shadow-xl shadow-black/20 transition hover:bg-zinc-950/30 active:scale-[0.98]"
            style={{ width: `${TILE_BUTTON_WIDTH_PX}px` }}
            title={t('Set as main image')}
            onClick={handleSetMainClick}
            onMouseDown={stopPrimaryDown}
            onPointerDown={stopPrimaryDown}
          >
            {t('Set as main image')}
          </button>
          {/* 右下角：拆分为新节点 */}
          <button
            type="button"
            className="absolute bottom-3 right-3 inline-flex items-center justify-center gap-2 aspect-[16/9] px-3 rounded-2xl border border-white/10 bg-zinc-950/25 backdrop-blur-xl text-[14px] font-semibold tracking-tight text-white/90 shadow-xl shadow-black/20 transition hover:bg-zinc-950/30 active:scale-[0.98]"
            style={{ width: `${TILE_BUTTON_WIDTH_PX}px` }}
            title={t('Split into node')}
            onClick={handleSplitClick}
            onMouseDown={stopPrimaryDown}
            onPointerDown={stopPrimaryDown}
          >
            {t('Split into node')}
          </button>
        </div>
      </ContextMenuTrigger>
      <ContextMenuContent className={FM_CONTEXT_MENU_SURFACE}>
        <ContextMenuItem onSelect={handleSetMain}>{t('Set as main image')}</ContextMenuItem>
        <ContextMenuItem onSelect={handleSplit}>{t('Split into node')}</ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  );
};

const ImageCompareTile = memo(ImageCompareTileInner);
